/**
 * Header chip that surfaces the user's Enterprise team status in the
 * dashboard top bar:
 *
 *  • Solo user (no workspaces) → small "Create team" link on `sm:` and up.
 *  • Exactly 1 workspace, user is lead → "Manage · {Team}" pill.
 *  • Exactly 1 workspace, user is member → readonly "In · {Team}" badge.
 *  • 2+ workspaces (Phase 3-ii multi-workspace) → dropdown that lists
 *    every team the user belongs to. Each entry navigates to that
 *    team's `/enterprise/team` page (lead-only target — members get a
 *    blocked redirect, by design).
 */
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Users, ChevronDown, Sparkles, AlertCircle } from "lucide-react";
import axios, { API } from "@/lib/api";

export default function TeamHeaderChip() {
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/workspaces/me`);
        if (cancelled) return;
        setWorkspaces(r.data?.workspaces || []);
        // Pull the user's active_workspace_id (best-effort).
        try {
          const me = await axios.get(`${API}/auth/me`);
          if (!cancelled) setActiveId(me.data?.active_workspace_id || null);
        } catch { /* ignore */ }
      } catch {
        // Silent — if /me fails, just don't render the chip.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Close the dropdown when clicking outside it.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Pick a workspace as the user's active context and navigate to its
  // team page. Members navigate to /app (their personal designer view).
  // If we're already on the destination URL, dispatch a custom event so
  // pages can reload their workspace-scoped state without a hard refresh.
  const switchTo = async (ws) => {
    setOpen(false);
    try {
      await axios.post(`${API}/workspaces/${ws.id}/switch`);
      setActiveId(ws.id);
    } catch {
      // Non-fatal — still navigate.
    }
    const dest = ws.is_lead ? "/enterprise/team" : "/app";
    if (window.location.pathname === dest) {
      window.dispatchEvent(new CustomEvent("facetra:workspace-switched",
                                            { detail: { workspace_id: ws.id } }));
    } else {
      navigate(dest);
    }
  };

  if (!loaded) return null;

  // Empty state — no workspaces at all.
  if (workspaces.length === 0) {
    return (
      <button
        type="button"
        onClick={() => navigate("/enterprise/team")}
        data-testid="team-header-create"
        title="Spin up a team workspace, invite designers, share your credit wallet"
        className="hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border border-white/15 text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition"
      >
        <Users className="w-3.5 h-3.5" /> Create team
      </button>
    );
  }

  // Single-workspace fast path — same UX as Phase 1.
  if (workspaces.length === 1) {
    const ws = workspaces[0];
    if (ws.is_lead) {
      return (
        <button
          type="button"
          onClick={() => navigate("/enterprise/team")}
          data-testid="team-header-manage"
          title="Manage members & distribute credits"
          className="hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border border-[#D4AF37]/45 text-[#D4AF37] bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 transition"
        >
          <Users className="w-3.5 h-3.5" /> {ws.name}
        </button>
      );
    }
    return (
      <span
        data-testid="team-header-member"
        title={`Designing in ${ws.name}`}
        className="hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border border-white/15 text-white/70"
      >
        <Users className="w-3.5 h-3.5" /> In · {ws.name}
      </span>
    );
  }

  // 2+ workspaces — dropdown with plan badge + cycle-status indicator.
  const expiredCount = workspaces.filter((w) => w.is_lead && w.cycle_active === false && w.plan !== "studio").length;
  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="team-header-switcher"
        title="Switch between your teams"
        aria-haspopup="menu"
        aria-expanded={open}
        className="hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border border-[#D4AF37]/45 text-[#D4AF37] bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 transition relative"
      >
        <Users className="w-3.5 h-3.5" />
        {workspaces.length} teams
        {expiredCount > 0 && (
          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-rose-400 ring-2 ring-[#070707]" data-testid="team-header-expired-dot" />
        )}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          role="menu"
          data-testid="team-header-switcher-menu"
          className="absolute top-full right-0 mt-2 z-50 min-w-[18rem] rounded-xl border border-white/10 bg-[#0E0E0E] shadow-2xl overflow-hidden"
        >
          <div className="px-3 py-2 text-[9px] uppercase tracking-[0.22em] text-white/45 border-b border-white/8">
            Your teams
          </div>
          {workspaces.map((ws) => {
            const isActive = activeId === ws.id;
            const billLapsed = ws.is_lead && ws.cycle_active === false && ws.billable;
            return (
              <button
                key={ws.id}
                type="button"
                role="menuitem"
                onClick={() => switchTo(ws)}
                data-testid={`team-header-switcher-item-${ws.id}`}
                className={`w-full text-left px-3 py-2.5 hover:bg-[#D4AF37]/10 transition border-b border-white/5 last:border-b-0 flex items-center justify-between gap-3 ${isActive ? "bg-[#D4AF37]/8" : ""}`}
              >
                <div className="min-w-0">
                  <div className="text-sm text-white flex items-center gap-1.5">
                    <span className="truncate">{ws.name}</span>
                    {isActive && (
                      <span className="text-[8px] uppercase tracking-[0.2em] text-[#D4AF37] bg-[#D4AF37]/15 border border-[#D4AF37]/40 rounded-full px-1.5 py-0.5">
                        active
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mt-0.5 flex items-center gap-1">
                    <Sparkles className="w-2.5 h-2.5 text-[#D4AF37]" />
                    {ws.plan_name || ws.plan}
                    <span className="text-white/30">·</span>
                    {ws.is_lead ? "Lead" : "Member"}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {billLapsed && (
                    <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] text-rose-300 bg-rose-500/15 border border-rose-400/40 rounded-full px-1.5 py-0.5" data-testid={`team-header-lapsed-${ws.id}`}>
                      <AlertCircle className="w-2.5 h-2.5" /> Renew
                    </span>
                  )}
                  {ws.is_lead && !billLapsed && (
                    <span className="text-[9px] uppercase tracking-[0.18em] text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-1.5 py-0.5">
                      Manage
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
